/**
 * CareerGanttChart Component
 *
 * Gantt-style chart showing work history with overlapping periods.
 */

import './charts.css'

interface GanttEntry {
  name: string
  company?: string
  start: number
  end: number
  color: string
  type?: 'employment' | 'founded' | 'co-founded' | 'contract'
}

interface CareerGanttChartProps {
  entries: GanttEntry[]
  startYear: number
  endYear: number
  title?: string
}

// Type icons
const typeIcons: Record<string, string> = {
  founded: '🚀',
  'co-founded': '🤝',
  employment: '💼',
  contract: '📋',
}

export function CareerGanttChart({
  entries,
  startYear,
  endYear,
  title,
}: CareerGanttChartProps) {
  const totalYears = endYear - startYear
  const barHeight = 28
  const barGap = 4
  const labelWidth = 180
  const chartWidth = 600
  const padding = { top: 40, right: 20, bottom: 30, left: labelWidth }

  // Calculate total height based on entries
  const chartHeight = entries.length * (barHeight + barGap) + padding.top + padding.bottom

  // Scale function: year to x position
  const yearToX = (year: number) => {
    const ratio = (year - startYear) / totalYears
    return padding.left + ratio * (chartWidth - padding.left - padding.right)
  }

  // Generate year ticks
  const yearTicks: number[] = []
  const tickInterval = totalYears > 20 ? 5 : 2
  for (let year = Math.ceil(startYear / tickInterval) * tickInterval; year <= endYear; year += tickInterval) {
    yearTicks.push(year)
  }

  // Sort entries by start date (oldest first)
  const sortedEntries = [...entries].sort((a, b) => a.start - b.start)

  return (
    <div className="gantt-chart">
      {title && <h3 className="chart-title">{title}</h3>}

      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="gantt-chart__svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Year grid lines and labels */}
        <g className="gantt-chart__grid">
          {yearTicks.map((year) => (
            <g key={year}>
              <line
                x1={yearToX(year)}
                y1={padding.top - 10}
                x2={yearToX(year)}
                y2={chartHeight - padding.bottom + 10}
                stroke="#e0e0e0"
                strokeWidth="1"
              />
              <text
                x={yearToX(year)}
                y={chartHeight - 10}
                textAnchor="middle"
                className="gantt-chart__tick-label"
              >
                {year}
              </text>
            </g>
          ))}
        </g>

        {/* "Now" line */}
        <line
          x1={yearToX(endYear)}
          y1={padding.top - 10}
          x2={yearToX(endYear)}
          y2={chartHeight - padding.bottom + 10}
          stroke="#007aff"
          strokeWidth="2"
          strokeDasharray="4 2"
        />
        <text
          x={yearToX(endYear)}
          y={padding.top - 15}
          textAnchor="middle"
          className="gantt-chart__now-label"
        >
          Now
        </text>

        {/* Gantt bars */}
        <g className="gantt-chart__bars">
          {sortedEntries.map((entry, index) => {
            const y = padding.top + index * (barHeight + barGap)
            const x1 = yearToX(entry.start)
            const x2 = yearToX(entry.end)
            const width = x2 - x1
            const icon = entry.type ? typeIcons[entry.type] || '' : ''

            return (
              <g key={`${entry.name}-${entry.start}`}>
                {/* Label */}
                <text
                  x={padding.left - 8}
                  y={y + barHeight / 2 + 4}
                  textAnchor="end"
                  className="gantt-chart__label"
                >
                  {icon && <tspan>{icon} </tspan>}
                  {entry.name.length > 20 ? entry.name.slice(0, 18) + '...' : entry.name}
                </text>

                {/* Bar */}
                <rect
                  x={x1}
                  y={y}
                  width={width}
                  height={barHeight}
                  rx={4}
                  fill={entry.color}
                  className="gantt-chart__bar"
                >
                  <title>
                    {entry.name}
                    {entry.company && ` at ${entry.company}`}
                    {'\n'}
                    {entry.start} - {entry.end === endYear ? 'Present' : entry.end}
                    {entry.type && `\nType: ${entry.type}`}
                  </title>
                </rect>

                {/* Type badge */}
                {entry.type && width > 60 && (
                  <text
                    x={x1 + 8}
                    y={y + barHeight / 2 + 4}
                    className="gantt-chart__type-badge"
                    fill="#fff"
                  >
                    {entry.type}
                  </text>
                )}
              </g>
            )
          })}
        </g>

        {/* Legend */}
        <g className="gantt-chart__legend" transform={`translate(${chartWidth - 150}, 10)`}>
          {Object.entries(typeIcons).map(([type, icon], index) => (
            <text
              key={type}
              x={index < 2 ? 0 : 70}
              y={index < 2 ? index * 14 : (index - 2) * 14}
              className="gantt-chart__legend-item"
            >
              {icon} {type}
            </text>
          ))}
        </g>
      </svg>
    </div>
  )
}

export default CareerGanttChart
